import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './header.css'
import Container from 'react-bootstrap/Container'
import Nav from 'react-bootstrap/Nav'
import Navbar from 'react-bootstrap/Navbar'
import { CORRECTIONS, DASHBOARD, REFER, SETTING, STORIES, WORDS } from 'routes/routeConstant'
import { AppUseDispatch, AppUseSelector } from '../../../redux/hooks'
import { SET_IS_MOB_NAV } from '../../../redux/types'
import { membershipNameType, PlanChangeLink } from '../../../utils/constant'
import { useLocation, useNavigate } from 'react-router-dom'
import useHasCorrections from '../../../hooks/useHasCorrections'
import { getReferralStatus, invalidateReferralStatus } from '../../../redux/actions/referralAction'
import NotificationBell from 'components/discussion/NotificationBell'
import { useLessonDiscussionsEnabled } from 'components/discussion/useLessonDiscussionsEnabled'

/** The gift in the "Refer a friend" link. */
const GiftIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <rect x='3' y='8' width='18' height='4' rx='1' />
    <path d='M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7' />
    <path d='M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5' />
  </svg>
)

const Header = () => {
  const { t } = useTranslation()
  const dispatch = AppUseDispatch()
  const navigate = useNavigate()
  const { pathname: pathName } = useLocation()
  const authToken = localStorage.getItem('authToken')
  const { isMobNav } = AppUseSelector(({ dashboard }) => dashboard)
  const { membership, credentials } = AppUseSelector(({ user }) => user)

  const planName = membership?.name

  const isPro = membership?.name == membershipNameType.Pro
  const isTeacher = membership?.name == membershipNameType.Teacher
  const isFree = membership?.name == membershipNameType.Free

  const discussionsEnabled = useLessonDiscussionsEnabled()

  // "Refer & earn" is the give-get referral, so only people who can refer (paid or trialling,
  // billed on the website) see it. The server decides. An upgrade, trial or downgrade in the same
  // session changes planName, which drops the cached status so it is asked for again.
  const [canRefer, setCanRefer] = useState(false)
  const lastPlanName = useRef(planName)
  useEffect(() => {
    if (lastPlanName.current !== undefined && lastPlanName.current !== planName) invalidateReferralStatus()
    lastPlanName.current = planName
    if (!authToken || isTeacher || isFree) {
      setCanRefer(false)
      return
    }
    let alive = true
    getReferralStatus().then((status) => alive && setCanRefer(!!status?.canRefer))
    return () => {
      alive = false
    }
  }, [authToken, isTeacher, isFree, planName])

  // Pro always has the corrections link. Anyone else gets it once they have a correction, finished
  // or still with the teacher: the free correction, or a Pro who downgraded.
  const hasCorrections = useHasCorrections(credentials?.memberId, !!authToken && !isTeacher && !!planName, {
    withPending: !isPro,
  })
  const showCorrections = isPro || hasCorrections

  // Replies, likes and returned corrections. Last in the row on desktop; beside the menu button on
  // phones, so the count shows without opening the menu. Anyone with corrections keeps it even with
  // discussions switched off, since it is where a returned correction is announced.
  const showBell = !!authToken && !!planName && (discussionsEnabled || showCorrections)

  return (
    <div className='header_main_div'>
      <Navbar collapseOnSelect expand='lg'>
        <Container className='mb-0'>
          <Navbar.Brand href={STORIES}>
            <label className='header_plan_text'>
              <img alt={t('iconAlt')} src={'/images/maayot-text-svg.svg'} />
            </label>
          </Navbar.Brand>
          {showBell && <NotificationBell />}
          {authToken && (
            <div className='header_nav_slot'>
              <div className='nav_toggle_div'>
                {isMobNav ? (
                  <Navbar.Toggle
                    onClick={() => dispatch({ type: SET_IS_MOB_NAV, payload: false })}
                    aria-controls='responsive-navbar-nav'
                    label={t('nav.toggleMenu')}
                  />
                ) : (
                  <Nav.Link className='main_link' onClick={() => dispatch({ type: SET_IS_MOB_NAV, payload: true })}>
                    <img alt={t('iconAlt')} src={'/images/close-icon.svg'} className='close_icon' />
                  </Nav.Link>
                )}
              </div>
              {isMobNav ? (
                <Navbar.Collapse id='responsive-navbar-nav' style={{ display: isMobNav ? 'none' : 'block' }}>
                  <div className='mob_header_div'>
                    <div />
                    <img
                      alt={t('iconAlt')}
                      src={'/images/close-icon.svg'}
                      className='close_icon'
                      onClick={() => dispatch({ type: SET_IS_MOB_NAV, payload: !isMobNav })}
                    />
                  </div>
                  <Nav className='ms-auto header_menu_div'>
                    {!isTeacher && (
                      <Nav.Link
                        onClick={() => {
                          navigate(WORDS)
                          dispatch({ type: SET_IS_MOB_NAV, payload: true })
                        }}
                      >
                        {t('nav.words')}
                      </Nav.Link>
                    )}
                    {showCorrections && (
                      <div className='disable-correction-container new-tooltip' tabIndex={1}>
                        <span
                          className='nav-link'
                          onClick={() => {
                            navigate(pathName?.includes(CORRECTIONS) ? STORIES : CORRECTIONS)
                            dispatch({ type: SET_IS_MOB_NAV, payload: true })
                          }}
                        >
                          {pathName?.includes(CORRECTIONS) ? t('nav.lessons') : t('nav.corrections')}
                        </span>
                      </div>
                    )}
                    {isTeacher ? (
                      <Nav.Link href={DASHBOARD}>{t('nav.dashboard')}</Nav.Link>
                    ) : (
                      <Nav.Link onClick={() => navigate(SETTING)}>{t('nav.settings')}</Nav.Link>
                    )}
                    {canRefer && (
                      <Nav.Link
                        className='header_refer_link'
                        title={t('nav.refer')}
                        onClick={() => {
                          navigate(REFER)
                          dispatch({ type: SET_IS_MOB_NAV, payload: true })
                        }}
                      >
                        <GiftIcon />
                        <span className='header_refer_label'>{t('nav.refer')}</span>
                      </Nav.Link>
                    )}
                    {isFree && (
                      <Nav.Link
                        className='header_upgrade_link'
                        onClick={() => {
                          navigate(PlanChangeLink)
                          dispatch({ type: SET_IS_MOB_NAV, payload: true })
                        }}
                      >
                        {t('nav.upgrade')}
                      </Nav.Link>
                    )}
                  </Nav>
                </Navbar.Collapse>
              ) : null}
            </div>
          )}
        </Container>
      </Navbar>
      {authToken && (
        <div className={!isMobNav ? 'show-menu' : 'show-menu hide'}>
          {!isMobNav ? (
            <div className='navbar-collapse1'>
              <div className='ms-auto header_menu_div' id='responsive-navbar-nav'>
                {!isTeacher && (
                  <Nav.Link
                    onClick={() => {
                      navigate(WORDS)
                      dispatch({ type: SET_IS_MOB_NAV, payload: true })
                    }}
                  >
                    {t('nav.words')}
                  </Nav.Link>
                )}
                {showCorrections && (
                  <div className='disable-correction-container new-tooltip' tabIndex={1}>
                    <span
                      className='nav-link'
                      onClick={() => {
                        navigate(pathName?.includes(CORRECTIONS) ? STORIES : CORRECTIONS)
                        dispatch({ type: SET_IS_MOB_NAV, payload: true })
                      }}
                    >
                      {pathName?.includes(CORRECTIONS) ? t('nav.lessons') : t('nav.corrections')}
                    </span>
                  </div>
                )}

                {isTeacher && (
                  <Nav.Link href={DASHBOARD}>{t('nav.dashboard')}</Nav.Link>
                )}
                {!isTeacher && (
                  <Nav.Link href={SETTING}>{t('nav.settings')}</Nav.Link>
                )}
                {canRefer && (
                  <Nav.Link
                    className='header_refer_link'
                    title={t('nav.refer')}
                    onClick={() => {
                      navigate(REFER)
                      dispatch({ type: SET_IS_MOB_NAV, payload: true })
                    }}
                  >
                    <GiftIcon />
                    <span className='header_refer_label'>{t('nav.refer')}</span>
                  </Nav.Link>
                )}
                {isFree && (
                  <Nav.Link href={PlanChangeLink} className='header_upgrade_link'>
                    {t('nav.upgrade')}
                  </Nav.Link>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default Header
